import { useState, useEffect } from "react";
import { Trash2, Pencil, Plus } from "lucide-react";
import { Button, Modal, Table } from "../../../components";
import axios from "axios";
import { GET_USERS_URL } from "../../../features/UrlHelper";
import { handleApiError } from "../../../features/errorHandler";

function UsersSection() {
  const [users, setUsers] = useState([]);
  const [loadingUsers, setLoadingUsers] = useState(true);
  const [deleteConfirm, setDeleteConfirm] = useState(null);

  // Add / Edit modal
  const [userModalOpen, setUserModalOpen] = useState(false);
  const [editingUser, setEditingUser] = useState(null);
  const [userForm, setUserForm] = useState({
    id: null,
    firstName: "",
    lastName: "",
    email: "",
    plan_name: "",
    valid_from: "",
    valid_to: "",
    is_verified: false,
    password: "",
  });

  var today = new Date();

  useEffect(() => {
    loadUsers();
  }, []);

  async function loadUsers() {
    try {
      const response = await axios.get(GET_USERS_URL, {
        headers: {
          Authorization: `Bearer ${window.localStorage.getItem("authToken")}`,
        },
      });
      setUsers(response.data);
    } catch (error) {
      handleApiError(error, "Nepodarilo sa načítať používateľov.");
    } finally {
      setLoadingUsers(false);
    }
  }

  const openAddUser = () => {
    setEditingUser(null);
    setUserForm({
      id: null,
      firstName: "",
      lastName: "",
      email: "",
      plan_name: "",
      valid_from: "",
      valid_to: "",
      is_verified: false,
      password: "",
    });
    setUserModalOpen(true);
  };

  const openEditUser = (user) => {
    setEditingUser(user);
    setUserForm({
      id: user.id,
      firstName: user.firstName || "",
      lastName: user.lastName || "",
      email: user.email || "",
      plan_name: user.plan_name || "",
      valid_from: user.valid_from ? user.valid_from.slice(0, 10) : "",
      valid_to: user.valid_to ? user.valid_to.slice(0, 10) : "",
      is_verified: user.is_verified || false,
      password: "", // Leave password empty when editing, only update if a new password is entered
    });
    setUserModalOpen(true);
  };

  const handleUserSubmit = (e) => {
    e.preventDefault();
    const data = {
      id: userForm.id,
      firstName: userForm.firstName,
      lastName: userForm.lastName,
      email: userForm.email,
      plan_name: userForm.plan_name,
      valid_from: userForm.valid_from,
      valid_to: userForm.valid_to,
      is_verified: userForm.is_verified,
      password: userForm.password, // Include password for both create and update (backend should handle it accordingly)
    };

    if (editingUser) {
      axios
        .post(GET_USERS_URL, data, {
          headers: {
            Authorization: `Bearer ${window.localStorage.getItem("authToken")}`,
          },
        })
        .then(() => loadUsers())
        .catch((error) => handleApiError(error, { showAlert: true, fallbackMessage: "Nepodarilo sa upraviť používateľa." }));
    } else {
      axios
        .put(GET_USERS_URL, data, {
          headers: {
            Authorization: `Bearer ${window.localStorage.getItem("authToken")}`,
          },
        })
        .then(() => loadUsers())
        .catch((error) => handleApiError(error, { showAlert: true, fallbackMessage: "Nepodarilo sa vytvoriť používateľa." }));
    }

    setUserModalOpen(false);
  };

  const deleteUser = (id) => {
    setUsers((prev) => prev.filter((u) => u.id !== id));
    setDeleteConfirm(null);
  };

  const nameStatus = (user) => {
    if (user.valid_from && new Date(user.valid_from) > today) {
      return (
        "Aktívne od " + new Date(user.valid_from).toLocaleDateString("sk-SK")
      );
    } else if (
      user.valid_from &&
      new Date(user.valid_from) <= today &&
      user.valid_to &&
      new Date(user.valid_to) >= today
    ) {
      return "Aktívne";
    } else {
      return "Neaktívne";
    }
  };

  return (
    <div className="admin__section">
      <div className="admin__sub-header">
        <h2 className="admin__section-title">Správa užívateľov</h2>
        <Button variant="primary" size="sm" onClick={openAddUser}>
          <Plus size={16} /> Pridať užívateľa
        </Button>
      </div>

      <div className="admin__table-wrapper">
        <Table
          columns={[
            { key: "firstName", label: "Meno" },
            { key: "lastName", label: "Priezvisko" },
            { key: "email", label: "Email" },
            { key: "plan_name", label: "Plán" },
            {
              key: "valid_from",
              label: "Platí Od",
              render: (val) => val !== null ? new Date(val).toLocaleDateString("sk-SK") : "-",
            },
            {
              key: "valid_to",
              label: "Platí Do",
              render: (val) => val !== null ? new Date(val).toLocaleDateString("sk-SK") : "-",
            },
            {
              key: "subscription_status",
              label: "Predplatné",
              sortable: false,
              render: (_, row) => (
                <span
                  className={`admin__status-badge ${row.valid_from && new Date(row.valid_from) <= today && new Date(row.valid_to) >= today ? "admin__status-badge--active" : ""}`}
                >
                  {nameStatus(row)}
                </span>
              ),
            },
            {
              key: "is_verified",
              label: "Overený",
              render: (val) => val ? "Áno" : "Nie",
            },
            {
              key: "actions",
              label: "Akcie",
              sortable: false,
              render: (_, row) => (
                <div className="admin__table-actions">
                  <button className="admin__icon-btn" onClick={() => openEditUser(row)} title="Upraviť">
                    <Pencil size={16} />
                  </button>
                  <button className="admin__icon-btn admin__icon-btn--danger" onClick={() => setDeleteConfirm(row.id)} title="Zmazať">
                    <Trash2 size={16} />
                  </button>
                </div>
              ),
            },
          ]}
          data={users}
          searchPlaceholder="Hľadať užívateľa..."
          emptyMessage="Žiadni užívatelia"
        />
      </div>

      {/* Add / Edit user modal */}
      <Modal
        isOpen={userModalOpen}
        onClose={() => setUserModalOpen(false)}
        title={editingUser ? "Upraviť užívateľa" : "Pridať užívateľa"}
      >
        <form className="admin__form" onSubmit={handleUserSubmit}>
          <div className="admin__form-row">
            <div className="admin__form-field">
              <label className="admin__form-label">Meno</label>
              <input
                maxLength={255}
                className="admin__form-input"
                value={userForm.firstName}
                onChange={(e) =>
                  setUserForm((prev) => ({
                    ...prev,
                    firstName: e.target.value,
                  }))
                }
                placeholder="Meno"
                required
              />
            </div>
            <div className="admin__form-field">
              <label className="admin__form-label">Priezvisko</label>
              <input
                maxLength={255}
                className="admin__form-input"
                value={userForm.lastName}
                onChange={(e) =>
                  setUserForm((prev) => ({
                    ...prev,
                    lastName: e.target.value,
                  }))
                }
                placeholder="Priezvisko"
                required
              />
            </div>
          </div>
          <div className="admin__form-field">
            <label className="admin__form-label">Email</label>
            <input
              maxLength={255}
              type="email"
              className="admin__form-input"
              value={userForm.email}
              onChange={(e) =>
                setUserForm((prev) => ({ ...prev, email: e.target.value }))
              }
              placeholder="email@example.com"
              required
              disabled={!!editingUser} // Disable email editing when updating user
              autoComplete="new-user-email"
            />
          </div>
          <div className="admin__form-field">
            <label className="admin__form-label">Heslo</label>
            <input
              maxLength={128}
              type="password"
              className="admin__form-input"
              value={userForm.password || ""}
              onChange={(e) =>
                setUserForm((prev) => ({ ...prev, password: e.target.value }))
              }
              placeholder="Heslo"
              autoComplete="off"
            //autoComplete="new-password"
            />
          </div>
          <div className="admin__form-field-checkbox">
            <label className="admin__form-label">Overený</label>
            <input
              type="checkbox"
              className="admin__form-checkbox"
              checked={userForm.is_verified || false}
              onChange={(e) =>
                setUserForm((prev) => ({
                  ...prev,
                  is_verified: e.target.checked,
                }))
              }
            />
          </div>

          <div className="admin__form-actions">
            <Button
              variant="ghost"
              type="button"
              onClick={() => setUserModalOpen(false)}
            >
              Zrušiť
            </Button>
            <Button variant="primary" type="submit">
              {editingUser ? "Uložiť zmeny" : "Pridať užívateľa"}
            </Button>
          </div>
        </form>
      </Modal>

      {/* Delete confirmation */}
      <Modal
        isOpen={deleteConfirm !== null}
        onClose={() => setDeleteConfirm(null)}
        title="Potvrdiť zmazanie"
      >
        <p>Naozaj chcete zmazať tohto užívateľa? Táto akcia sa nedá vrátiť.</p>
        <div className="admin__form-actions">
          <Button variant="ghost" onClick={() => setDeleteConfirm(null)}>
            Zrušiť
          </Button>
          <Button
            variant="primary"
            onClick={() => deleteUser(deleteConfirm)}
            style={{ backgroundColor: "#EF4444", borderColor: "#EF4444" }}
          >
            Zmazať
          </Button>
        </div>
      </Modal>
    </div>
  );
}

export default UsersSection;
